import {EmploymentStatus, EmploymentStatusOther, MilitaryBranches} from '@deliveriesondemand/dod-data';
import {calculateDuration} from '@deliveriesondemand/dod-utils';
import {connect} from 'react-redux';
import {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import handlers from 'react-handlers';
import {formatMoney} from 'accounting';
import injectSheet from 'react-jss';
import moment from 'moment';

import Step from '-/components/general/Step';
import Select from '-/components/fields/Select';
import Address from '-/components/general/Address';
import PhoneInput from '-/components/fields/PhoneInput';
import {NumberInput} from '-/components/fields/NumberInput';
import DateInput from '-/components/fields/DateInput';
import {renderFieldErrors} from '-/util/util';
import TextInput from '-/components/fields/TextInput';
import NavigationService from '-/services/NavigationService';
import BuyerInfoService from '-/services/BuyerInfoService';
import EmploymentInfoService from '-/services/EmploymentInfoService';
import UIService from '-/services/UIService';
import {Control, ControlGroup} from '-/components/fields/Control';
import {RadioGroup, Radio} from '-/components/fields/RadioGroup';
import ApiService from '-/services/ApiService';
import PriorEmployment from '-/components/fields/PriorEmployment';
import PayInfo from '-/components/fields/PayInfo';
import CreditorService from '-/services/CreditorService';
import Form from '-/components/fields/Form';
import Legend from '-/components/fields/Legend';
import AdditionalIncomeService from '-/services/AdditionalIncomeService';
import AdditionalIncomeSection from '-/components/connected/steps/AdditionalIncomeSection';
import {get, isFinite} from '-/util/lodash';



const styles = () => ({
    warning: {
        backgroundColor: 'white',
        border: '2px solid red',
        fontWeight: 'bold',
        marginBottom: 15,
        padding: 10,
        textAlign: 'center'
    }
});

class EmploymentInfo extends Component {
    static propTypes = {
        onSaveAndExitClick: PropTypes.func,
        onReviewClick: PropTypes.func
    };

    static defaultProps = {
        onSaveAndExitClick: () => {},
        onReviewClick: () => {}
    };

    constructor(props) {
        super(props);

        this.state = {
            errorMessages: [],
            showErrors: false
        };

        this.fieldRefs = {};

        this.onNextClick = this.onNextClick.bind(this);
        this.onSaveAndExitClick = this.onSaveAndExitClick.bind(this);
        this.onReviewClick = this.onReviewClick.bind(this);
        this.onInputChange = this.onInputChange.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const {isCoApplicant} = this.props;

        if (nextProps.isCoApplicant !== isCoApplicant) {
            this.setState({
                errorMessages: [],
                showErrors: false
            });
        }
    }

    getErrorMessages() {
        const {buyerInfo, provideSelfEmploymentVerification, requirePositionForRetirement, yearsWorked} = this.props;
        // employment info
        const {
            additionalIncomeSection,
            dutyTransferExpectedYes,
            dutyTransferWhen,
            dutyTransferWhere,
            employerAddress,
            employerName,
            employerPhone,
            employmentStatus,
            actualEndDate,            
            militaryBase,
            monthsRetired,
            payGrade,
            position,
            priorEmployment,
            actualStartDate,
            typeOfBusiness,
            yearsRetired
        } = this.fieldRefs;

        const status = employmentStatus.value;
        let messages = employmentStatus.getErrors('Employment Status');
        if (status === 'employed') {
            messages = [...messages, ...employerName.getErrors('Employer name')];
            messages = [...messages, ...employerAddress.getErrors('Employer address')];
            messages = [...messages, ...employerPhone.getErrors('Employer phone')];
            messages = [...messages, ...position.getErrors('Employee position')];
        } else if (status === 'selfEmployed') {
            messages = [...messages, ...typeOfBusiness.getErrors('Type of Business')];

            const seVerification = CreditorService.getSelfEmploymentVerification() || {};
            const {isActive} = seVerification;

            if (isActive && !provideSelfEmploymentVerification) {
                messages = [...messages, 'You must agree to provide tax returns'];
            }
        } else if (status === 'activeMilitary') {
            messages = [
                ...messages,
                ...employerAddress.getErrors('Employer address'),
                ...position.getErrors('Position/Title'),
                ...payGrade.getErrors('Pay Grade'),
                ...militaryBase.getErrors('Military Base')
            ];
            if (dutyTransferExpectedYes.checked) {
                messages = [
                    ...messages,
                    ...dutyTransferWhere.getErrors('Duty Transfer Where'),
                    ...dutyTransferWhen.getErrors('Duty Transfer When')
                ];
            }
        } else if (status === 'retired') {
            const {entries = []} = this.props;

            if (requirePositionForRetirement) {
                if (!position.value) {
                    messages = [...messages, ...position.getErrors('Prior employment position')];
                    messages = [...messages, ...employerName.getErrors('Employer name')];
                }

                if (!actualEndDate.value) {
                    messages = [...messages, ...actualEndDate.getErrors('Prior position end date is required')];
                }

                if (!actualStartDate.value) {
                    messages = [...messages, ...actualStartDate.getErrors('Prior position start date is required')];
                }

                if (actualStartDate.value && actualStartDate.value) {
                    const start = moment(actualStartDate.value, 'MM/DD/YYYY');
                    const end = moment(actualEndDate.value, 'MM/DD/YYYY');
                    
                    if (end.isBefore(start)) {
                        messages = [...messages, 'Prior position end date must be after start date'];
                    }
                }
            }

            for (let i = 0; i < entries.length; i++) {
                const el = additionalIncomeSection.refs[`item${i}`];

                messages = [...messages, ...el.getErrors(`Retirement income item #${i + 1}`)];
            }

            if (!isFinite(yearsRetired.value) && !isFinite(monthsRetired.value)) {
                messages = [...messages, ...yearsRetired.getErrors('Years retired')];
            }
        }

        if (yearsWorked < CreditorService.getYearsWorkHistoryRequired() && status !== 'retired') {
            messages = [...messages, ...this.fieldRefs.priorEmployment.getErrors()];
        }

        if (status !== 'retired' && status !== 'other') {
            // pay
            const {yearsWorked, monthsWorked, payInfo} = this.fieldRefs;
            const workedSuffix = status === 'activeMilitary' ? 'Served' : 'Worked';
            // duration
            if (yearsWorked && monthsWorked) {
                const months = parseFloat(monthsWorked.value) || 0;
                const years = parseFloat(yearsWorked.value) || 0;
                // if months is empty or 0, make sure we validate years
                if (isNaN(months) || months <= 0) {
                    messages = [...messages, ...yearsWorked.getErrors(`Years ${workedSuffix}`)];
                }
                // if years is empty or 0, make sure we validate months
                
                if (isNaN(years) || years <= 0) {
                    messages = [...messages, ...monthsWorked.getErrors(`Months ${workedSuffix}`)];
                }

                if (!isNaN(months) && !isNaN(years)) {
                    const {dob} = buyerInfo;
                    const previousEmployment = get(priorEmployment, 'state.value', []) || [];
                    const {months: totalMonths, years: totalYears} = calculateDuration(years, months);
                    const totalRegularMonthsWorked = (totalYears * 12) + totalMonths;
                    const totalPreviousMonthsWorked = previousEmployment.reduce((totalMonths, item) => {
                        const {priorEmployerMonthsWorked: months = 0, priorEmployerYearsWorked: years = 0} = item;

                        return totalMonths + ((years * 12) + months);
                    }, 0);
                    const totalMonthsWorked = totalRegularMonthsWorked + totalPreviousMonthsWorked;
                    const earliestAllowedStart = moment(dob).add(15 * 12, 'months').startOf('month');
                    const earliestActualStart = moment().subtract(totalMonthsWorked, 'months').startOf('month');
                    const isUnderage = earliestActualStart.isBefore(earliestAllowedStart);

                    if (isUnderage) {
                        messages = [...messages, 'Only work history starting from age 15 is allowed.'];
                    }
                }
            }

            messages = [...messages, ...payInfo.getErrors()];
        }

        return messages;
    }

    onInputChange() {
        setTimeout(() => {
            const {showErrors} = this.state;

            if (showErrors) {
                const errorMessages = this.getErrorMessages();
                this.setState({errorMessages});
            }
        }, 0);
    }

    onClickIfValid(fn) {
        const errorMessages = this.getErrorMessages();
        this.setState({errorMessages, showErrors: true});
        if (errorMessages.length <= 0) {
            fn();
        }
    }

    onSaveAndExitClick() {
        const {onSaveAndExitClick: fn} = this.props;

        fn();
    }

    onNextClick() {
        this.onClickIfValid(() => {
            ApiService.save('employmentInfo').then(() => {
                UIService.stepCompleted('employmentInfo');
                NavigationService.next('additionalIncome');
            });
        });
    }

    onReviewClick() {
        const {onReviewClick: fn} = this.props;

        this.onClickIfValid(fn);
    }

    isRetired() {
        const {status} = this.props;
        return status === 'retired';
    }

    getNonRetiredFields() {
        const {
            classes,
            status,
            onYearsWorkedChange,
            onMonthsWorkedChange,
            pay,
            yearsWorked,
            monthsWorked
        } = this.props;

        const seVerification = CreditorService.getSelfEmploymentVerification() || {};
        const {isActive, years} = seVerification;

        if (this.isRetired()) {
            return (
                <div>
                    <p className="info">We hope you are enjoying your retirement!</p>
                </div>
            );
        } else if (status === 'other') {
            return null;
        } else {
            const msgs = {
                activeMilitary:' Thanks for your service! How long have you been in the military?',
                selfEmployed: 'How long have you been working for yourself?'
            };
            const defaultMsg = 'How long have you been working there?';
            const msg = msgs[status] ? msgs[status] : defaultMsg;
            const {
                contactPhone,
                netOrGross,
                onNetOrGrossChange,
                provideSelfEmploymentVerification,
                onProvideSelfEmploymentVerificationChange
            } = this.props;

            return (
                <div>
                    <ControlGroup labelWidth={200}>
                        <Legend>{msg}</Legend>
                        <Control>
                            <NumberInput
                                ref={ref => this.fieldRefs.yearsWorked = ref}
                                label="Years"
                                placeholder="X Years"
                                value={yearsWorked}
                                onChange={handlers(onYearsWorkedChange, this.onInputChange)}
                            />
                        </Control>
                        <Control>
                            <NumberInput
                                ref={ref => this.fieldRefs.monthsWorked = ref}
                                label="Months"
                                placeholder="X Months"
                                value={monthsWorked}
                                onChange={handlers(onMonthsWorkedChange, this.onInputChange)}
                            />
                        </Control>
                    </ControlGroup>
                    {status === 'selfEmployed' && isActive &&
                        <Fragment>
                            <ControlGroup>
                                <Legend>Are you able to provide {years} years worth of taxes for this current self employed business?</Legend>
                                <RadioGroup>
                                    <Radio label="Yes" name="provideSelfEmploymentVerification" value="yes" checked={provideSelfEmploymentVerification} onChange={handlers(onProvideSelfEmploymentVerificationChange, this.onInputChange)} />
                                    <Radio label="No" name="provideSelfEmploymentVerification" value="no" checked={provideSelfEmploymentVerification === false} onChange={handlers(onProvideSelfEmploymentVerificationChange, this.onInputChange)} />
                                </RadioGroup>
                            </ControlGroup>
                            {provideSelfEmploymentVerification === false && (
                                <p className={classes.warning}>
                                    You must have {years} years worth of self employed taxes for your business. We&apos;re sorry we can&apos;t move forward with your auto refinance loan. If you have any questions please call <a href={`tel:${contactPhone}`}>{contactPhone}</a> to talk to an auto loan refinance representative.
                                </p>
                            )}
                        </Fragment>
                    }
                    <PayInfo ref={ref => this.fieldRefs.payInfo = ref} pay={pay} onChange={this.onInputChange} />
                    <ControlGroup>
                        <Legend>Is this amount before or after taxes?</Legend>
                        <RadioGroup>
                            <Radio label="Before" name="netOrGross" value="gross" id="gross" checked={netOrGross === 'gross'} onChange={handlers(onNetOrGrossChange, this.onInputChange)} />
                            <Radio label="After" name="netOrGross" value="net" id="net" checked={netOrGross === 'net'} onChange={handlers(onNetOrGrossChange, this.onInputChange)} />
                        </RadioGroup>
                    </ControlGroup>
                </div>
            );
        }
    }

    getPriorEmployment() {
        const {
            status,
            priorEmployment,
            onPriorEmploymentChange,
            monthsWorked: monthsWorkedRaw,
            yearsWorked: yearsWorkedRaw
        } = this.props;
        const yearsWorked = parseInt(yearsWorkedRaw, 10) || 0;
        const monthsWorked = parseInt(monthsWorkedRaw, 10) || 0;
        const yearsRequired = CreditorService.getYearsWorkHistoryRequired();
        const monthsRequired = yearsRequired * 12;
        const totalMonthsWorked = (yearsWorked * 12) + monthsWorked;

        if (status === 'retired') {
            return null;
        } else if (status === 'other') {
            // if "other", allow prior employment but don't force a limit on years
            return (
                <PriorEmployment
                    ref={ref => this.fieldRefs.priorEmployment = ref}
                    onChange={handlers(onPriorEmploymentChange, this.onInputChange)}
                    value={priorEmployment}
                    monthsNeeded={0}
                    priorEmploymentRequired={false} />
            );
        } else if (yearsWorked < yearsRequired) {
            return (
                <PriorEmployment
                    ref={ref => this.fieldRefs.priorEmployment = ref}
                    onChange={handlers(onPriorEmploymentChange, this.onInputChange)}
                    value={priorEmployment}
                    monthsNeeded={monthsRequired - totalMonthsWorked}
                    priorEmploymentRequired={true} />
            );
        } else {
            return null;
        }
    }

    getDutyStationTransferFields() {
        const {
            status,
            dutyTransferExpected,
            onDutyTransferExpectedChange,
            dutyTransferWhere,
            onDutyTransferWhereChange,
            dutyTransferWhen,
            onDutyTransferWhenChange
        } = this.props;

        if (status === 'activeMilitary') {
            return (
                <ControlGroup labelWidth={200}>
                    <Legend>Is duty station transfer expected during the next year?</Legend>
                    <RadioGroup>
                        <Radio
                            ref={ref => this.fieldRefs.dutyTransferExpectedYes = ref}
                            name="dutyTransferExpected"
                            value="true"
                            label="Yes"
                            id="dutyTransferExpectedYes"
                            checked={!!dutyTransferExpected}
                            onChange={handlers(onDutyTransferExpectedChange, this.onInputChange)} />
                        <Radio
                            name="dutyTransferExpected"
                            value="false"
                            label="No"
                            id="dutyTransferExpected"
                            checked={!dutyTransferExpected}
                            onChange={handlers(onDutyTransferExpectedChange, this.onInputChange)} />
                    </RadioGroup>
                    {dutyTransferExpected ? (
                        <Control>
                            <TextInput
                                ref={ref => this.fieldRefs.dutyTransferWhere = ref}
                                label="Where"
                                placeholder="Location"
                                value={dutyTransferWhere}
                                onChange={handlers(onDutyTransferWhereChange, this.onInputChange)} />
                        </Control>
                    ) : null}
                    {dutyTransferExpected ? (
                        <Control>
                            <DateInput
                                ref={ref => this.fieldRefs.dutyTransferWhen = ref}
                                label="When"
                                placeholder="MM/DD/YYYY"
                                value={dutyTransferWhen}
                                onChange={handlers(onDutyTransferWhenChange, this.onInputChange)} />
                        </Control>
                    ) : null}
                </ControlGroup>
            );
        }

        return null;
    }

    render() {
        const {
            apiErrors = [],
            lifeTimeSavings,
            onPreviousClick,
            onEmploymentStatusChange,
            status,
            onEmploymentStatusOtherChange,
            statusOther,
            employerAddress,
            onAddressChange,
            employerName,
            onEmployerNameChange,
            employerPhone,
            onEmployerPhoneChange,
            position,
            onPositionChange,
            requirePositionForRetirement,
            // selfEmployed Specific fields
            typeOfBusiness,
            onTypeOfBusinessChange,
            // military specific fields
            onMilitaryBaseChange,
            onMilitaryBranchChange,
            militaryBase,
            militaryBranch,
            payGrade,
            onPayGradeChange,
            onMonthsRetiredChange,
            onEndDateChange,
            onStartDateChange,
            onYearsRetiredChange
        } = this.props;
        const retirementMonths = get(this.props, 'retirement.duration.months', 0);
        const retirementYears = get(this.props, 'retirement.duration.years', 0);
        const actualEndDate = get(this.props, 'actualEndDate', null);
        const actualStartDate = get(this.props, 'actualStartDate', null);
        const {errorMessages, showErrors} = this.state;
        let fields = [];

        if (status === 'employed') {
            fields.push((
                <Control key="employer-name">
                    <TextInput
                        ref={ref => this.fieldRefs.employerName = ref}
                        label="Employer Name"
                        placeholder="Company"
                        value={employerName}
                        onChange={handlers(onEmployerNameChange, this.onInputChange)} />
                </Control>
            ));
            fields.push((
                <Address
                    key="employer-address"
                    ref={ref => this.fieldRefs.employerAddress = ref}
                    value={employerAddress}
                    onChange={handlers(onAddressChange, this.onInputChange)} />
            ));
            fields.push((
                <Control key="employer-phone">
                    <PhoneInput
                        ref={ref => this.fieldRefs.employerPhone = ref}
                        label="Employer Phone"
                        placeholder="XXX-XXX-XXXX"
                        value={employerPhone}
                        onChange={handlers(onEmployerPhoneChange, this.onInputChange)} />
                </Control>
            ));
            fields.push((
                <Control key="employed-position">
                    <TextInput
                        ref={ref => this.fieldRefs.position = ref}
                        label="Position/Title"
                        placeholder="Salesperson"
                        value={position}
                        onChange={handlers(onPositionChange, this.onInputChange)} />
                </Control>
            ));
        } else if (status === 'selfEmployed') {
            fields.push((
                <Control key="self-employed-type">
                    <TextInput
                        ref={ref => this.fieldRefs.typeOfBusiness = ref}
                        label="Type of Business"
                        placeholder="Construction, Retail, Service, etc"
                        value={typeOfBusiness}
                        onChange={handlers(onTypeOfBusinessChange, this.onInputChange)} />
                </Control>
            ));
            fields.push((
                <Control key="self-employed-name">
                    <TextInput
                        ref={ref => this.fieldRefs.employerName = ref}
                        label="Company Name"
                        placeholder="Company"
                        value={employerName}
                        onChange={handlers(onEmployerNameChange, this.onInputChange)} />
                </Control>
            ));
            fields.push((
                <Address
                    key="self-employed-address"
                    ref={ref => this.fieldRefs.employerAddress = ref}
                    value={employerAddress}
                    onChange={handlers(onAddressChange, this.onInputChange)} />
            ));
        } else if (status === 'activeMilitary') {
            fields.push((
                <ControlGroup key="military-employment" labelWidth={200}>
                    <Control key="military-branch">
                        <Select
                            label="Military Branch"
                            innerRef={ref => this.fieldRefs.militaryBranch = ref}
                            onChange={handlers(onMilitaryBranchChange, this.onInputChange)}>
                            <option omitFromList key="makeSelection">Please select your branch</option>
                            {Object.keys(MilitaryBranches).map((branch, i) => {
                                return (
                                    <option
                                        key={`${i}-${branch}`}
                                        selected={branch === militaryBranch}
                                        value={branch}>
                                        {MilitaryBranches[branch]}
                                    </option>
                                );
                            })}
                        </Select>
                    </Control>
                    <Control key="military-position">
                        <TextInput
                            ref={ref => this.fieldRefs.position = ref}
                            label="Position/Title"
                            value={position}
                            onChange={handlers(onPositionChange, this.onInputChange)} />
                    </Control>
                    <Control key="military-paygrade">
                        <TextInput
                            ref={ref => this.fieldRefs.payGrade = ref}
                            label="Pay Grade"
                            placeholder="E-1, W-4, O-6, etc"
                            value={payGrade || ''}
                            onChange={handlers(onPayGradeChange, this.onInputChange)} />
                    </Control>
                    <Control key="military-base">
                        <TextInput
                            ref={ref => this.fieldRefs.militaryBase = ref}
                            label="Military Base"
                            placeholder="Fort Riley, Fort Leavenworth, etc"
                            value={militaryBase || ''}
                            onChange={handlers(onMilitaryBaseChange, this.onInputChange)} />
                    </Control>
                    <Control key="military-base-phone">
                        <PhoneInput
                            ref={ref => this.fieldRefs.employerPhone = ref}
                            label="Military Base Phone"
                            placeholder="XXX-XXX-XXXX"
                            value={employerPhone}
                            onChange={handlers(onEmployerPhoneChange, this.onInputChange)} />
                    </Control>
                    <Address
                        key="military-base-address"
                        ref={ref => this.fieldRefs.employerAddress = ref}
                        value={employerAddress}
                        onChange={handlers(onAddressChange, this.onInputChange)} />
                </ControlGroup>
            ));
        } else if (status === 'other') {
            fields.push(
                <Control key="other-employment">
                    <Select
                        label="More Details"
                        innerRef={ref => this.fieldRefs.employmentStatusOther = ref}
                        onChange={handlers(onEmploymentStatusOtherChange, this.onInputChange)}>
                        <option omitFromList key="makeSelection">Please tell us more</option>
                        {Object.keys(EmploymentStatusOther).map((eStatus, i) => {
                            return (
                                <option
                                    key={`${i}-${eStatus}`}
                                    selected={eStatus === statusOther}
                                    value={eStatus}>
                                    {EmploymentStatusOther[eStatus]}
                                </option>
                            );
                        })}
                    </Select>
                </Control>
            );
        } else if (status === 'retired') {
            if (requirePositionForRetirement) {
                fields.push((
                    <Fragment>
                        <Control key="retired-previous-company" labelWidth={200}>
                            <TextInput
                                ref={ref => this.fieldRefs.employerName = ref}
                                label="Prior Company"
                                placeholder="Sears"
                                value={employerName}
                                onChange={handlers(onEmployerNameChange, this.onInputChange)} />
                        </Control>
                        <Control key="retired-previous-position" labelWidth={200}>
                            <TextInput
                                ref={ref => this.fieldRefs.position = ref}
                                label="Prior Position"
                                placeholder="Salesperson"
                                value={position}
                                onChange={handlers(onPositionChange, this.onInputChange)} />
                        </Control>
                        <Control key="retired-start-date" labelWidth={200}>
                            <DateInput
                                ref={ref => this.fieldRefs.actualStartDate = ref}
                                label="Start Date"
                                placeholder="MM/DD/YYYY"
                                value={actualStartDate}
                                onChange={handlers(onStartDateChange, this.onInputChange)} />
                        </Control>
                        <Control key="retired-end-date" labelWidth={200}>
                            <DateInput
                                ref={ref => this.fieldRefs.actualEndDate = ref}
                                label="End Date"
                                placeholder="MM/DD/YYYY"
                                value={actualEndDate}
                                onChange={handlers(onEndDateChange, this.onInputChange)} />
                        </Control>
                    </Fragment>
                ));
            }

            fields.push((
                <div key="retirement-duration" style={{marginTop: 20}}>
                    <ControlGroup labelWidth={200}>
                        <Legend>How long have you been retired?</Legend>
                        <Control>
                            <NumberInput
                                ref={ref => this.fieldRefs.yearsRetired = ref}
                                label="Years"
                                placeholder="X Years"
                                value={retirementYears}
                                onChange={handlers(onYearsRetiredChange, this.onInputChange)}
                            />
                        </Control>
                        <Control>
                            <NumberInput
                                ref={ref => this.fieldRefs.monthsRetired = ref}
                                label="Months"
                                placeholder="X Months"
                                value={retirementMonths}
                                onChange={handlers(onMonthsRetiredChange, this.onInputChange)}
                            />
                        </Control>
                    </ControlGroup>
                </div>
            ));

            fields.push(
                <div key="additional-income-outer" style={{marginTop: 20}}>
                    <Legend>Retirement Income</Legend>
                    <AdditionalIncomeSection
                        addText="Add More Retirement"
                        key="additional-income"
                        mode="employment"
                        onInputChange={this.onInputChange}
                        innerRef={ref => this.fieldRefs['additionalIncomeSection'] = ref}
                        totalText="Total Retirement Income"
                        typeFilter={type => ['retirementSocialSecurity', 'retirementPension'].includes(type)} />
                </div>
            );
        }

        let savingsMessage = (
            <p className="info">Congratulations! You&apos;re almost done completing your application.</p>
        );

        if (lifeTimeSavings && lifeTimeSavings > 1000) {
            savingsMessage = (
                <p className="info">Congratulations! You are about to lock in around <b>{formatMoney(lifeTimeSavings)}</b> of savings.</p>
            );
        }

        return (
            <Step
                onSaveAndExitClick={this.onSaveAndExitClick}
                onPreviousClick={onPreviousClick}
                onNextClick={this.onNextClick}
                onReviewClick={this.onReviewClick}>
                <Form>
                    <ControlGroup labelWidth={200}>
                        <Legend>Employment Information</Legend>
                        {savingsMessage}
                        <Control>
                            <Select
                                label="Status"
                                innerRef={ref => this.fieldRefs.employmentStatus = ref}
                                onChange={handlers(onEmploymentStatusChange, this.onInputChange)}>
                                <option omitFromList key="makeSelection">Please select an employment status</option>
                                {Object.keys(EmploymentStatus).map((eStatus, i) => {
                                    return (
                                        <option
                                            key={i}
                                            selected={eStatus === status}
                                            value={eStatus}>
                                            {EmploymentStatus[eStatus]}
                                        </option>
                                    );
                                })}
                            </Select>
                        </Control>
                        {fields}
                    </ControlGroup>
                    {this.getDutyStationTransferFields()}
                    {this.getNonRetiredFields()}
                    {this.getPriorEmployment()}
                    {showErrors ? renderFieldErrors([...errorMessages, ...apiErrors]) : null}
                </Form>
            </Step>
        );
    }
}

const mapStateToProps = state => {
    const {config = {}, dealInfo = {}} = state;
    const {lifeTimeSavings} = dealInfo;
    const {contactPhone} = config;
    const buyerInfo = BuyerInfoService.getBuyerInfoForCurrentApplicant();
    const entries = AdditionalIncomeService.getFilteredEntries(['retirementSocialSecurity', 'retirementPension', '']);
    const requirePositionForRetirement = CreditorService.getPositionForRetirementRequired();
    const apiErrors = ApiService.getErrors();

    return {
        apiErrors,
        buyerInfo,
        contactPhone,
        entries,
        requirePositionForRetirement,
        ...EmploymentInfoService.getEmploymentInfoForCurrentApplicant(),
        isCoApplicant: NavigationService.isCoApplicant(),
        lifeTimeSavings
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onPriorEmploymentChange: value => {
            if (Array.isArray(value)) {
                for (var i = 0; i < value.length; i++) {
                    const employer = value[i];
                    const {priorEmployerMonthsWorked: monthsRaw, priorEmployerYearsWorked: yearsRaw} = employer;
                    const months = isFinite(monthsRaw) ? monthsRaw : 0;
                    const years = isFinite(yearsRaw) ? yearsRaw : 0;
                    // enforce numbers
                    value[i].priorEmployerMonthsWorked = months;
                    value[i].priorEmployerYearsWorked = years;
                }
            }

            EmploymentInfoService.set('priorEmployment', value);
        },
        onNetOrGrossChange: e => {
            const {value, checked} = e.target;
            if (checked) {
                EmploymentInfoService.set('netOrGross', value);
            }
        },
        onProvideSelfEmploymentVerificationChange: e => {
            const {value, checked} = e.target;

            if (checked) {
                EmploymentInfoService.set('provideSelfEmploymentVerification', value === 'yes' ? true : false);
            }
        },
        onMilitaryBranchChange: value => {
            EmploymentInfoService.set('militaryBranch', value);
        },
        onPayGradeChange: value => {
            EmploymentInfoService.set('payGrade', value);
        },
        onMilitaryBaseChange: value => {
            EmploymentInfoService.set('militaryBase', value);
        },
        onYearsWorkedChange: value => {
            EmploymentInfoService.set('yearsWorked', parseInt(value, 10));
        },
        onMonthsWorkedChange: value => {
            EmploymentInfoService.set('monthsWorked', parseInt(value, 10));
        },
        onEmployerPhoneChange: value => {
            EmploymentInfoService.set('employerPhone', value);
        },
        onPositionChange: value => {
            EmploymentInfoService.set('position', value);
        },
        onEmployerNameChange: value => {
            EmploymentInfoService.set('employerName', value);
        },
        onAddressChange: value => {
            EmploymentInfoService.set('employerAddress', value);
        },
        onEmploymentStatusChange: value => {
            EmploymentInfoService.set('status', value);
        },
        onEmploymentStatusOtherChange: value => {
            EmploymentInfoService.set('statusOther', value);
        },
        onTypeOfBusinessChange: value => {
            EmploymentInfoService.set('typeOfBusiness', value);
        },
        onDutyTransferExpectedChange: e => {
            const {value, checked} = e.target;
            if (checked) {
                const dutyTransferExpected = value === 'true';
                EmploymentInfoService.set('dutyTransferExpected', dutyTransferExpected);
            }
        },
        onDutyTransferWhereChange: value => {
            EmploymentInfoService.set('dutyTransferWhere', value);
        },
        onDutyTransferWhenChange: value => {
            EmploymentInfoService.set('dutyTransferWhen', value);
        },
        onEndDateChange: value => {
            EmploymentInfoService.set('actualEndDate', value);
        },
        onStartDateChange: value => {
            EmploymentInfoService.set('actualStartDate', value);
        },
        onMonthsRetiredChange: value => {
            EmploymentInfoService.set('retirement.duration.months', value);
        },
        onYearsRetiredChange: value => {
            EmploymentInfoService.set('retirement.duration.years', value);
        },
        onPreviousClick: () => {
            dispatch(() => {
                const state = CreditorService.getState();
                const isDMSActive = get(state, 'creditor.isDMSActive', false);
                const isCoApplicant = NavigationService.isCoApplicant();
                const nextStep = isCoApplicant || isDMSActive ? 'buyerInfo' : 'vehicleInfo';

                NavigationService.next(nextStep);
                ApiService.save('none');
            });
        },
        onSaveAndExitClick: () => {
            ApiService.explicitSave('employmentInfo');
        },
        onReviewClick: () => {
            NavigationService.next('finalReview');
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(injectSheet(styles)(EmploymentInfo));
